import React, { useRef, useState } from "react";
import EmailEditor, { EditorRef } from "react-email-editor";

import useSWRMutation from "swr/mutation";
import {  ThreeDots } from "react-loader-spinner";
import s from "./ReactEmailEditor.module.scss";
import { UseAuthToken } from "../../../../../../../../libs/api/userSession";
import { parseClientError, swrMutationFetcher } from "../../../../../../../../libs/api/Endpoints";
import { SuccessDataType } from "../../../../../../../../libs/api/types";
import { generateResult } from "../../../../../../../../global components/ResultModal/ResultModal";


interface UnlayerFile {
  attach: Blob;
  name: string;
}

type ImageUploadDone = (data: { url: string } | { error: string }) => void;

type RichTextEditorProps = {
  handleEditorHTMLValue: (value: string, jsonFomat: any) => void;
  jsonFormat?: any;
};

function ReactEmailEditor({
  handleEditorHTMLValue,
  jsonFormat,
}: RichTextEditorProps) {
  const [editorLoaded, setEditorLoaded] = useState(false);

  const emailEditorRef = useRef<EditorRef>(null);
  const token = UseAuthToken();
  const { trigger: uploadImage } = useSWRMutation(
    "api/admin/medias",
    swrMutationFetcher
  );

  async function handleImage(blob: Blob) {
    type Response = { secureUrl: string };
    if (blob) {
      const file= new File([blob], "", { type: blob.type });
      const formData = new FormData();
      formData.append("filemedia", file);
      formData.append("type", "image");

      try {
        const response: SuccessDataType<Response> = await uploadImage({
          token,
          requestOption: { method: "POST", body: formData },
        });
        return response.data.secureUrl;
      } catch (error) {
        const e = parseClientError(error);
        generateResult("error", e.message);
        return null;
      }
    }
  }

  const onReady = () => {
    const unlayer = emailEditorRef.current?.editor;
    if (!unlayer) return;
    setEditorLoaded(true);

    unlayer.registerCallback(
      "image",
      async (file: UnlayerFile, done: ImageUploadDone) => {
        const imageUrl = await handleImage(file.attach);
        if (imageUrl) {
          done({ url: imageUrl });
        } else {
          done({ error: "Image upload failed" });
        }
      }
    );
    let design;
    // Design structure with optional content passed in
    if (jsonFormat && Object.keys(jsonFormat).length > 0) {
      design = jsonFormat;
    }
    if (design) {
      unlayer.loadDesign(design);
    }

    unlayer.addEventListener("design:updated", () => {
      unlayer.exportHtml((data) => {
        const { html, design } = data;
        let cleanHtml = html;
        cleanHtml = cleanHtml.replace(/<body[^>]*>/i, (match) => {
          // Strip background and style attributes
          let newTag = match.replace(/\sbackground="[^"]*"/gi, "");
          return newTag;
        });

        cleanHtml = cleanHtml.replace(
          /<body([^>]*)>/i,
          `<body$1 style="margin:0;padding:0;background:none;">`
        );

        handleEditorHTMLValue(cleanHtml, design);
      });
    });
  };

  // --- Watch for content changes ---

  return (
    <div className={s.wrapper}>
      {!editorLoaded && (
        <div className={s.loading}>
          <ThreeDots
            visible
            height="30"
            width="30"
            color="#d52b1e"
            ariaLabel="three-dots-loading"
          />
          <span>Loading editor...</span>
        </div>
      )}

      {<div className={s.overlay}></div>}
      <EmailEditor
        ref={emailEditorRef}
        onReady={onReady}
        style={{ height: "74vh" }}
        options={{
          appearance: {
            theme: "light",
          },
          tools: {
            body: {
              properties: {
                backgroundColor: {
                  display: false,
                },
              },
            },
          },
        }}
      />
    </div>
  );
}

export default ReactEmailEditor;
